import {useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField} from "@mui/material";
import {useHistory} from "react-router-dom";
import BigSelector, {BigAction, BigSelectorItem} from "../../components/common/BigSelector/BigSelector";
import {useIsSuper} from "../../state/UserContextProvider";
import {useModuleEditorApi} from "./api";
import {CopyModuleDialog} from "./CopyModuleDialog";
import type {ModuleDescriptor} from "./types";

const MODULES_QUERY_KEY = "module-editor-list";

type CreateModuleDialogProps = {
    open: boolean;
    onClose: (created?: { moduleId: string; moduleName: string }) => void;
};

function CreateModuleDialog({open, onClose}: CreateModuleDialogProps) {
    const [moduleId, setModuleId] = useState("");
    const [moduleName, setModuleName] = useState("");

    function handleCreate() {
        if (moduleId.trim() && moduleName.trim()) {
            onClose({moduleId: moduleId.trim(), moduleName: moduleName.trim()});
            setModuleId("");
            setModuleName("");
        }
    }

    function handleCancel() {
        onClose();
        setModuleId("");
        setModuleName("");
    }

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Module</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Module ID"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={moduleId}
                    onChange={(e) => setModuleId(e.target.value)}
                    placeholder="my-module"
                />
                <TextField
                    margin="dense"
                    label="Module Name"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    placeholder="My Module"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleCreate} variant="contained" disabled={!moduleId.trim() || !moduleName.trim()}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function VersionBadges({module}: { module: ModuleDescriptor }) {
    var badges = [];
    var versions = module.versions || {};

    if (versions["TEST"]) {
        badges.push(<Chip key="test" label="TEST" size="small" color="warning" variant="outlined"/>);
    }
    if (versions["PROD"]) {
        badges.push(<Chip key="prod" label="PROD" size="small" color="success" variant="outlined"/>);
    }

    return <>{badges}</>;
}

export function ModulesListPage() {
    const api = useModuleEditorApi();
    const client = useQueryClient();
    const history = useHistory();
    const isSuper = useIsSuper();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [copyTarget, setCopyTarget] = useState<ModuleDescriptor | null>(null);
    const [working, setWorking] = useState(false);
    const [search, setSearch] = useState("");

    const {isFetching, isError, error, data: modules} = useQuery(
        [MODULES_QUERY_KEY],
        () => api.listModules(),
        {staleTime: 30000}
    );

    if (isError) {
        console.error(error);
        return <Alert severity="error">Failed to load modules</Alert>;
    }

    var actions: BigAction[] = [
        {
            icon: "fas fa-sync",
            onClick: async () => {
                await client.invalidateQueries([MODULES_QUERY_KEY]);
            }
        },
        {
            icon: "fas fa-plus",
            onClick: () => setDialogOpen(true),
        },
    ];

    var items: BigSelectorItem[] = [];
    if (!isFetching && modules) {
        var term = search.toLowerCase();
        var filtered = term
            ? modules.filter((m) => m.name.toLowerCase().includes(term) || m.module_id.toLowerCase().includes(term))
            : modules;
        var sorted = [...filtered].sort((a, b) => {
            var aDate = a.versions?.["HEAD"]?.promoted_on ?? "";
            var bDate = b.versions?.["HEAD"]?.promoted_on ?? "";
            return bDate.localeCompare(aDate);
        });
        for (const mod of sorted) {
            var inline = [<VersionBadges key="badges" module={mod}/>];
            if (isSuper) {
                inline.push(
                    <IconButton
                        key="copy"
                        size="small"
                        title="Copy to another namespace"
                        onClick={(e) => {
                            e.stopPropagation();
                            setCopyTarget(mod);
                        }}
                    >
                        <i className="fas fa-copy"/>
                    </IconButton>
                );
            }
            items.push({
                id: `${mod.namespace}/${mod.module_id}`,
                name: mod.name,
                subtitle: `${mod.namespace}/${mod.module_id}`,
                icon: "fas fa-cube",
                inlineComponents: inline,
                onActivate: () => {
                    history.push(`/modules/${mod.namespace}/${mod.module_id}`);
                },
            });
        }
    }

    async function handleDialogClose(created?: { moduleId: string; moduleName: string }) {
        setDialogOpen(false);
        if (created) {
            try {
                setWorking(true);
                await api.createModule(created.moduleId, {
                    module_name: created.moduleName,
                    scope: "private",
                    script: `function main() {\n  done("hello world");\n}\n`,
                    description_md: "",
                    settings: null,
                    edit_schema: null,
                    node_props: null,
                });
                await client.invalidateQueries([MODULES_QUERY_KEY]);
                history.push(`/modules/${modules?.[0]?.namespace ?? ""}/${created.moduleId}`);
            } finally {
                setWorking(false);
            }
        }
    }

    return (
        <>
            <Box sx={{maxWidth: 1024, width: "90%", mx: "auto", mt: 2}}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search modules..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <i className="fas fa-search" style={{marginRight: 8, opacity: 0.5}}/>,
                    }}
                />
            </Box>
            <BigSelector
                title="Modules"
                emptyMessage="No modules found. Create one to get started."
                items={items}
                actions={actions}
                loading={isFetching || working}
            />
            <CreateModuleDialog open={dialogOpen} onClose={handleDialogClose}/>
            {copyTarget && (
                <CopyModuleDialog
                    open={true}
                    onClose={() => setCopyTarget(null)}
                    module={copyTarget}
                />
            )}
        </>
    );
}
